import React, { useCallback, useMemo, useRef } from 'react';
import { ProfileIds } from 'types/ipi.types';
import { ContentContainer } from 'app/components/Core/ChakraContentContainer';
import { useContextMenu } from 'app/components/ChakraTableList';
import { ContentContainerType } from 'types/enums';
import { ReactComponent as IdsIcon } from 'resources/images/id.svg';
import { ChakraEmptyList } from 'app/components/Core/ChakraEmptyList';
import { Link } from '@chakra-ui/react';
import { useIdsListState } from 'app/components/Profile/Ids/IdsList/hooks';
import { IdsListDialogs } from 'app/components/Profile/Ids/IdsListDialogs';
import {
  getColumns,
  getNormalizedProfileIds,
} from 'app/components/Profile/Ids/IdsList/utils';
import { AppDispatch, useAppSelector } from 'app/store';
import { Row } from '@tanstack/react-table';
import { contextMenuHandler } from 'app/components/Table/ContextMenuButton/utils';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { getPreferredIds } from 'app/store/Profile/selectors';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { AddIdNumberFab } from '../AddIdNumberFab';
import { ProfileIdsTableRow, RowType } from './ProfileIdsTableRow';

export interface IdsListProps {
  profileIds: ProfileIds;
  loading?: boolean;
  dispatch: AppDispatch;
  showEmptyState?: boolean;
}

export const IdsList: React.FC<IdsListProps> = ({
  profileIds,
  loading,
  showEmptyState,
  dispatch,
}) => {
  const {
    addId,
    v3Claim,
    handleIdDialogClose,
    updateV3ClaimVerificationData,
    onEmailClaimDialogClose,
    verificationDisclosure,
    emailConfirmDisclosure,
    onConfirmClaimDialogClose,
    requestGuidForVerification,
    ipnProps,
    isniProps,
    ipiProps,
    setAddId,
  } = useIdsListState(dispatch);

  const ref = useRef<HTMLDivElement>(null);
  const tableKey = 'profile-ids';

  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(() => getColumns(totalWidth), [totalWidth]);
  const contextMenuProps = useContextMenu();

  const rowRenderer = useCallback(
    (row: Row<RowType>) => {
      const { options } = row.original;

      return (
        <ProfileIdsTableRow
          key={row.id}
          row={row}
          contextHandler={(e) =>
            contextMenuHandler(e, contextMenuProps, options)
          }
        />
      );
    },
    [contextMenuProps]
  );

  const preferredIds = useAppSelector(getPreferredIds);

  const rows = useMemo(
    () =>
      getNormalizedProfileIds(
        profileIds,
        ipiProps,
        ipnProps,
        isniProps,
        preferredIds
      ),
    [ipiProps, ipnProps, isniProps, profileIds, preferredIds]
  );

  return (
    <>
      <ContentContainer
        type={ContentContainerType.LIST}
        isViewContent
        pt={4}
        pb={0}
      >
        {showEmptyState ? (
          <ChakraEmptyList
            mt={104}
            icon={<IdsIcon role="img" />}
            title="Getting Credit"
            primaryText={
              <>
                Add your ID below to ensure you get credited and paid correctly.
                <br />
                <Link
                  href="https://support.sessionstudio.com/en/articles/7053006-music-industry-identifiers"
                  isExternal
                  fontWeight={400}
                >
                  Learn more about IDs here.
                </Link>
              </>
            }
          />
        ) : (
          <StickyTable<RowType>
            tableKey={tableKey}
            ref={ref}
            data={rows}
            columns={columns}
            rowRenderer={rowRenderer}
            loading={loading}
            contextMenuProps={contextMenuProps}
          />
        )}
        <AddIdNumberFab onSelect={setAddId} />
      </ContentContainer>
      <IdsListDialogs
        addId={addId}
        ipnProps={ipnProps}
        ipiProps={ipiProps}
        isniProps={isniProps}
        updateV3ClaimVerificationData={updateV3ClaimVerificationData}
        handleIdDialogClose={handleIdDialogClose}
        v3Claim={v3Claim}
        onEmailClaimDialogClose={onEmailClaimDialogClose}
        onConfirmClaimDialogClose={onConfirmClaimDialogClose}
        verificationDisclosure={verificationDisclosure}
        emailConfirmDisclosure={emailConfirmDisclosure}
        requestGuidForVerification={requestGuidForVerification}
      />
    </>
  );
};
